import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, MessageCircle } from "lucide-react";
import productsData from "../data/products.json";
import siteSettings from "../data/site-settings.json";
import { PRODUCTS } from "../constants/testIds";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

const ProductsPage = () => {
  const { categoryId } = useParams();
  const [activeCategory, setActiveCategory] = useState(categoryId || "all");

  const categories = productsData.categories;
  const filteredCategories =
    activeCategory === "all"
      ? categories
      : categories.filter((c) => c.slug === activeCategory);

  const handleWhatsApp = (message) => {
    window.open(
      `https://wa.me/${siteSettings.whatsapp}?text=${encodeURIComponent(message)}`,
      "_blank"
    );
  };

  return (
    <div className="pt-20" data-testid={PRODUCTS.pageSection}>
      {/* Hero */}
      <section className="relative bg-deepCharcoal py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={siteSettings.store_interior}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-deepCharcoal/70 to-deepCharcoal/90" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.span variants={fadeUp} className="section-label" style={{ color: "#C9A96E" }}>
              Our Catalog
            </motion.span>
            <motion.h1
              variants={fadeUp}
              className="font-cormorant text-5xl sm:text-6xl font-bold text-white mt-3 mb-4"
            >
              Premium Food Products
            </motion.h1>
            <motion.div variants={fadeUp} className="gold-divider mx-auto mb-5" />
            <motion.p
              variants={fadeUp}
              className="font-outfit text-base text-white/70 max-w-xl mx-auto"
            >
              Browse our wide selection of quality food commodities available for wholesale and retail purchase.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter Tabs */}
      <section className="sticky top-20 z-30 bg-shadowWhite border-b border-goldBorder shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="flex overflow-x-auto gap-0 scrollbar-hide"
            data-testid={PRODUCTS.categoryFilter}
          >
            <button
              onClick={() => setActiveCategory("all")}
              className={`flex-shrink-0 px-5 py-4 font-outfit text-sm font-medium transition-colors border-b-2 ${
                activeCategory === "all"
                  ? "text-premiumGold border-premiumGold"
                  : "text-[#4A4A4A] border-transparent hover:text-premiumGold"
              }`}
              data-testid={PRODUCTS.categoryTab}
            >
              All Products
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.slug)}
                className={`flex-shrink-0 px-5 py-4 font-outfit text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                  activeCategory === cat.slug
                    ? "text-premiumGold border-premiumGold"
                    : "text-[#4A4A4A] border-transparent hover:text-premiumGold"
                }`}
                data-testid={PRODUCTS.categoryTab}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-16 bg-shadowWhite" data-testid={PRODUCTS.productGrid}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          {filteredCategories.map((cat, idx) => (
            <motion.div
              key={cat.id}
              id={cat.slug}
              initial="hidden"
              whileInView="visible"
              variants={stagger}
              viewport={{ once: true, margin: "-50px" }}
            >
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${idx % 2 !== 0 ? "lg:grid-flow-col-dense" : ""}`}>
                {/* Image */}
                <motion.div
                  variants={fadeUp}
                  className={`relative overflow-hidden ${idx % 2 !== 0 ? "lg:col-start-2" : ""}`}
                >
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-72 lg:h-80 object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-deepCharcoal/30 to-transparent" />
                  {/* Gold corner */}
                  <span className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-premiumGold" />
                  <span className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-premiumGold" />
                </motion.div>

                {/* Content */}
                <motion.div variants={stagger} className={idx % 2 !== 0 ? "lg:col-start-1 lg:row-start-1" : ""}>
                  <motion.span variants={fadeUp} className="section-label">Category {String(idx + 1).padStart(2, "0")}</motion.span>
                  <motion.h2
                    variants={fadeUp}
                    className="font-cormorant text-4xl font-bold text-deepCharcoal mt-2 mb-3"
                  >
                    {cat.name}
                  </motion.h2>
                  <motion.div variants={fadeUp} className="gold-divider mb-4" />
                  <motion.p variants={fadeUp} className="font-outfit text-base text-[#4A4A4A] leading-relaxed mb-6">
                    {cat.description}
                  </motion.p>

                  {/* Highlights */}
                  <motion.div
                    variants={stagger}
                    className="grid grid-cols-2 gap-2 mb-8"
                  >
                    {cat.highlights.map((item, i) => (
                      <motion.div
                        key={i}
                        variants={fadeUp}
                        className="flex items-center space-x-2"
                      >
                        <CheckCircle size={14} className="text-premiumGold flex-shrink-0" />
                        <span className="font-outfit text-sm text-[#4A4A4A]">{item}</span>
                      </motion.div>
                    ))}
                  </motion.div>

                  {/* Bulk Info */}
                  <motion.div
                    variants={fadeUp}
                    className="bg-softIvory border border-goldBorder p-4 mb-6"
                  >
                    <p className="font-outfit text-xs text-premiumGold font-semibold uppercase tracking-wide mb-1">
                      Bulk Supply Available
                    </p>
                    <p className="font-outfit text-sm text-[#4A4A4A]">
                      We offer competitive bulk pricing for wholesale orders. Contact us for custom quotes and delivery arrangements.
                    </p>
                  </motion.div>

                  {/* CTA */}
                  <motion.div variants={fadeUp}>
                    <button
                      onClick={() => handleWhatsApp(cat.whatsapp_message)}
                      className="inline-flex items-center space-x-2 bg-premiumGold text-deepCharcoal px-7 py-3.5 font-outfit font-semibold text-sm hover:bg-[#B8944A] transition-colors duration-200"
                      data-testid={PRODUCTS.inquiryBtn}
                    >
                      <MessageCircle size={16} />
                      <span>Inquire on WhatsApp</span>
                    </button>
                  </motion.div>
                </motion.div>
              </div>

              {/* Divider */}
              {idx < filteredCategories.length - 1 && (
                <div className="border-t border-goldBorder mt-20" />
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-softIvory py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={stagger}
            viewport={{ once: true }}
          >
            <motion.h2
              variants={fadeUp}
              className="font-cormorant text-4xl font-bold text-deepCharcoal mb-4"
            >
              Need a Custom Quote?
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="font-outfit text-base text-[#4A4A4A] mb-8 leading-relaxed"
            >
              For large orders, bulk pricing, or specific product requirements, contact us directly on WhatsApp for the fastest response.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`https://wa.me/${siteSettings.whatsapp}?text=${encodeURIComponent(
                  "Hello AL ZAHRAN FLOUR MILL, I would like a custom quote for bulk products."
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center space-x-2 bg-premiumGold text-deepCharcoal px-8 py-3.5 font-outfit font-semibold text-sm hover:bg-[#B8944A] transition-colors"
              >
                <MessageCircle size={16} />
                <span>Get Bulk Quote</span>
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center space-x-2 border border-deepCharcoal text-deepCharcoal px-8 py-3.5 font-outfit font-semibold text-sm hover:bg-deepCharcoal hover:text-white transition-all"
              >
                <span>Contact Us</span>
                <ArrowRight size={15} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
